/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import { Boton, Titulo, accessAPI, downloadFile, subirArchivosPesadosS3, SubidorArchivos, ArchivoPreview, Sidebar, Formulario, TablaFiltrable } from "../../../shared";

export default function TarjetaArchivos({ proceso, archivos, onArchivosChange }) {
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [descargandoArchivoId, setDescargandoArchivoId] = useState(null);

  // Sidebar de subida: el archivo se sube con una categoría elegida de las
  // configuradas por el organismo, o una custom escrita a mano (que pisa a la
  // del dropdown)
  const [sideBarSubir, setSideBarSubir] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [formLoaderSubir, setFormLoaderSubir] = useState(false);
  const [formKeySubir, setFormKeySubir] = useState(0);

  // Sidebar de edición de categoría de un archivo ya subido
  const [sideBarCategoria, setSideBarCategoria] = useState(false);
  const [formLoaderCategoria, setFormLoaderCategoria] = useState(false);
  const [formKeyCategoria, setFormKeyCategoria] = useState(0);

  const toast = useRef(null);
  const menuRef = useRef(null);

  // Carga todas las categorías de archivos del organismo
  useEffect(() => {
    accessAPI(
      "GET",
      "admin/config/categoriasarchivos?dropdown=true",
      null,
      (respuesta) => {
        setCategorias(respuesta);
      },
      () => {}
    );
  }, []);

  const menuItems = [
    {
      label: "Descargar",
      icon: "pi pi-download",
      command: () => {
        if (archivoSeleccionado) {
          handleDescargar(archivoSeleccionado);
        }
      }
    },
    {
      label: "Editar categoría",
      icon: "pi pi-bookmark",
      command: () => {
        if (archivoSeleccionado) {
          setFormKeyCategoria((k) => k + 1);
          setSideBarCategoria(true);
        }
      }
    },
    {
      label: archivoSeleccionado?.disponibleEnActividades
        ? "Quitar de actividades"
        : "Disponible en actividades",
      icon: archivoSeleccionado?.disponibleEnActividades
        ? "pi pi-eye-slash"
        : "pi pi-eye",
      command: () => {
        if (archivoSeleccionado) {
          handleToggleDisponibilidad(archivoSeleccionado.id);
        }
      }
    },
    {
      label: archivoSeleccionado?.disponibleParaClientes
        ? "Quitar de clientes"
        : "Disponible para clientes",
      icon: archivoSeleccionado?.disponibleParaClientes
        ? "pi pi-eye-slash"
        : "pi pi-eye",
      command: () => {
        if (archivoSeleccionado) {
          handleToggleDisponibilidadClientes(archivoSeleccionado.id);
        }
      }
    },
    {
      separator: true
    },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        if (archivoSeleccionado) {
          confirmarEliminar(archivoSeleccionado);
        }
      }
    }
  ];

  // Sube el archivo del formulario con su categoría (subida directa a S3 vía
  // presigned URL) y confirma contra la API. La categoría custom, si se
  // escribió, pisa a la seleccionada en el dropdown.
  function subirArchivo(contenidoCampos, archivo) {
    if (!archivo) {
      return;
    }
    setFormLoaderSubir(true);
    subirArchivosPesadosS3({
      presignEndpoint: "admin/archivo/presign",
      confirmarEndpoint: `admin/proceso/${proceso.id}/archivo/confirmar`,
      archivos: archivo,
      datosPresign: { ubicacion: "archivosprocesos" },
      datosConfirmar: {
        categoria:
          contenidoCampos.categoriaCustom?.trim() ||
          contenidoCampos.categoria ||
          null
      }
    })
      .then((creados) => {
        if (onArchivosChange) {
          onArchivosChange([...archivos, ...creados]);
        }
        setFormLoaderSubir(false);
        setSideBarSubir(false);
        toast.current?.show({
          severity: "success",
          summary: "Subido",
          detail: "Archivo subido exitosamente",
          life: 3000
        });
      })
      .catch((error) => {
        setFormLoaderSubir(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.[0]?.msg || "Error al subir archivo",
          sticky: true
        });
      });
  }

  // Guarda la nueva categoría del archivo seleccionado. La categoría custom,
  // si se escribió, pisa a la seleccionada en el dropdown.
  function editarCategoria(contenidoCampos) {
    const categoria =
      contenidoCampos.categoriaCustom?.trim() ||
      contenidoCampos.categoria ||
      null;
    setFormLoaderCategoria(true);
    accessAPI(
      "PUT",
      `admin/proceso/${proceso.id}/archivo/${archivoSeleccionado.id}/categoria`,
      { categoria },
      (respuesta) => {
        if (onArchivosChange) {
          onArchivosChange(
            archivos.map((a) =>
              a.id === archivoSeleccionado.id
                ? { ...a, categoria: respuesta.categoria }
                : a
            )
          );
        }
        setFormLoaderCategoria(false);
        setSideBarCategoria(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Categoría actualizada correctamente",
          life: 3000
        });
      },
      (error) => {
        setFormLoaderCategoria(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.[0]?.msg || "Error al actualizar categoría",
          sticky: true
        });
      }
    );
  }

  function handleDescargar(archivo) {
    setDescargandoArchivoId(archivo.id);
    downloadFile(
      `admin/archivo/${archivo.id}`,
      archivo.nombredescarga,
      () => setDescargandoArchivoId(null),
      (error) => {
        setDescargandoArchivoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al descargar archivo",
          sticky: true
        });
      }
    );
  }

  function handleToggleDisponibilidad(archivoId) {
    accessAPI(
      "PUT",
      `admin/proceso/${proceso.id}/archivo/${archivoId}/disponibilidad`,
      null,
      (respuesta) => {
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: respuesta.disponibleEnActividades
            ? "Archivo disponible en actividades"
            : "Archivo no disponible en actividades"
        });
        if (onArchivosChange) {
          const nuevosArchivos = archivos.map((a) =>
            a.id === archivoId
              ? { ...a, disponibleEnActividades: respuesta.disponibleEnActividades }
              : a
          );
          onArchivosChange(nuevosArchivos);
        }
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al actualizar disponibilidad",
          sticky: true
        });
      }
    );
  }

  function handleToggleDisponibilidadClientes(archivoId) {
    accessAPI(
      "PUT",
      `admin/proceso/${proceso.id}/archivo/${archivoId}/disponibilidadclientes`,
      null,
      (respuesta) => {
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: respuesta.disponibleParaClientes
            ? "Archivo disponible para clientes"
            : "Archivo no disponible para clientes"
        });
        if (onArchivosChange) {
          const nuevosArchivos = archivos.map((a) =>
            a.id === archivoId
              ? { ...a, disponibleParaClientes: respuesta.disponibleParaClientes }
              : a
          );
          onArchivosChange(nuevosArchivos);
        }
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al actualizar disponibilidad",
          sticky: true
        });
      }
    );
  }

  function confirmarEliminar(archivo) {
    confirmDialog({
      message: `¿Está seguro que desea eliminar "${archivo.nombredescarga}"?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      accept: () => handleEliminar(archivo.id)
    });
  }

  function handleEliminar(archivoId) {
    accessAPI(
      "DELETE",
      `admin/proceso/${proceso.id}/archivo/${archivoId}`,
      null,
      () => {
        toast.current?.show({
          severity: "success",
          summary: "Eliminado",
          detail: "Archivo eliminado exitosamente"
        });
        if (onArchivosChange) {
          const nuevosArchivos = archivos.filter((a) => a.id !== archivoId);
          onArchivosChange(nuevosArchivos);
        }
      },
      (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al eliminar archivo",
          sticky: true
        });
      }
    );
  }

  const accionesTemplate = (rowData) => {
    const descargando = descargandoArchivoId === rowData.id;
    return (
      <div css={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5em" }}>
        {descargando && <i className="pi pi-spin pi-spinner" />}
        <Boton
          icono
          negro
          callback={(e) => {
            setArchivoSeleccionado(rowData);
            menuRef.current.toggle(e);
          }}
        >
          <i className="pi pi-ellipsis-v" />
        </Boton>
      </div>
    );
  };

  const checkTemplate = (field) => (rowData) => (
    rowData[field]
      ? <i className="pi pi-check" css={{ color: "var(--verde)" }} />
      : null
  );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={menuItems} popup ref={menuRef} />

      {/* Sidebar de subida de archivo con categoría */}
      <Sidebar
        visible={sideBarSubir}
        onHide={() => setSideBarSubir(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Subir archivo</Titulo>
        <Formulario
          key={formKeySubir}
          config={{
            secciones: [
              {
                campos: [
                  {
                    nombre: "categoria",
                    etiqueta: "Categoría",
                    tipoitemformularioid: 5, // dropdown
                    opciones: categorias,
                    sinOpcionInicial: true
                  },
                  {
                    nombre: "categoriaCustom",
                    etiqueta: "Categoría custom",
                    tipoitemformularioid: 1,
                    notas: "Opcional, reemplaza a la seleccionada"
                  },
                  {
                    nombre: "archivo",
                    etiqueta: "Archivo",
                    tipoitemformularioid: 10, // file
                    requerido: true
                  }
                ]
              }
            ],
            botones: [
              {
                etiqueta: "Subir archivo",
                callback: subirArchivo,
                validar: true
              }
            ]
          }}
          loader={formLoaderSubir}
        />
      </Sidebar>

      {/* Sidebar de edición de categoría de un archivo */}
      <Sidebar
        visible={sideBarCategoria}
        onHide={() => setSideBarCategoria(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar categoría</Titulo>
        {archivoSeleccionado && (
          <>
            <p css={{ marginTop: 0 }}>
              Categoría actual:{" "}
              <strong>{archivoSeleccionado.categoria || "sin categoría"}</strong>
            </p>
            <Formulario
              key={formKeyCategoria}
              config={{
                secciones: [
                  {
                    campos: [
                      {
                        nombre: "categoria",
                        etiqueta: "Categoría",
                        tipoitemformularioid: 5, // dropdown
                        opciones: categorias,
                        sinOpcionInicial: true
                      },
                      {
                        nombre: "categoriaCustom",
                        etiqueta: "Categoría custom",
                        tipoitemformularioid: 1,
                        notas: "Opcional, reemplaza a la seleccionada"
                      }
                    ]
                  }
                ],
                botones: [
                  {
                    etiqueta: "Guardar categoría",
                    callback: editarCategoria
                  }
                ]
              }}
              loader={formLoaderCategoria}
            />
          </>
        )}
      </Sidebar>

      <Card>
        <Titulo
          botones={
            <>
              {/* Subida múltiple sin categoría: filepicker directo */}
              <SubidorArchivos
                presignEndpoint="admin/archivo/presign"
                confirmarEndpoint={`admin/proceso/${proceso.id}/archivo/confirmar`}
                ubicacion="archivosprocesos"
                multiple
                chooseLabel="Multi"
                texto
                onSubido={(creados) => {
                  if (onArchivosChange) {
                    onArchivosChange([...archivos, ...creados]);
                  }
                }}
              />
              <Boton
                icono
                negro
                callback={() => {
                  setFormKeySubir((k) => k + 1);
                  setSideBarSubir(true);
                }}
              >
                <i className="pi pi-plus"></i>
              </Boton>
            </>
          }
        >
          Archivos del proceso
        </Titulo>

        {archivos && archivos.length > 0 ? (
          <TablaFiltrable conFiltros={false} key={`archivos-${descargandoArchivoId}`} value={archivos} size="small" nombreArchivo="archivos_proceso">
            <Column
              field="nombredescarga"
              header="Nombre"
              body={(rowData) => (
                <ArchivoPreview
                  nombre={rowData.nombredescarga}
                  hash={rowData.hash}
                  etiqueta={rowData.categoria || undefined}
                  subtitulo={rowData.categoria ? rowData.nombredescarga : undefined}
                />
              )}
            />
            <Column
              header="Act."
              body={checkTemplate("disponibleEnActividades")}
              style={{ width: "4rem", textAlign: "center" }}
            />
            <Column
              header="Cli."
              body={checkTemplate("disponibleParaClientes")}
              style={{ width: "4rem", textAlign: "center" }}
            />
            <Column
              body={accionesTemplate}
              style={{ width: "4rem", textAlign: "center" }}
            />
          </TablaFiltrable>
        ) : (
          <p css={{ color: "var(--grisTextos)" }}>
            No hay archivos registrados
          </p>
        )}
      </Card>
    </>
  );
}
